
<template>
	<el-row>
		<el-col :span="24" style="text-align: left;">
			<div class="base-form header">
				<h2 class="item" v-if="isCreatingMode">
					Новая запчасть <span>{{ruleForm?.partNumber}}</span>
				</h2>
				<h2 class="item" v-else>
					Карточка запчасти <span>{{item?.partNumber}}</span>
				</h2>
			</div>
		</el-col>		
	</el-row>
	<el-row>
		<el-col :span="24">
			<div class="base-form form">
				<el-form :model="ruleForm" ref="addUserForm" v-loading="isAsyncOperationPending" :rules="rules">
					<el-form-item label="Артикул" label-position="top" prop="partNumber">						
						<el-row style="width:100%">
							<el-col :span="23">
								<el-input v-model="ruleForm.partNumber"	
									:readonly="isSearchByNumberLoading"	
									:clearable="!isSearchByNumberLoading"
									maxlength="255"
									@clear="onPartNumberClear"
									@keyup="onPartNumberKeyup" />
							</el-col>
							<el-col v-if="!isManualFillingFormModeOn" :span="1" align="middle" style="padding: 0.36rem 0px 0px 0.28rem;">
								<el-icon v-if="isSearchByNumberLoading" size="17" class="is-loading"><Loading /></el-icon>							
								<el-icon v-if="!isSearchByNumberLoading" 
									size="20" 
									@click="() => onPartNumberProcessClick(ruleForm.partNumber)" 
									:class="{'item-processor': true, 'disabled': ruleForm.partNumber?.length < itemNumberMinLength}">
										<Cpu />
								</el-icon>
							</el-col>
							<el-col v-else :span="1" align="middle" style="padding: 0.36rem 0px 0px 0.28rem;">								
								<el-icon size="20" class="item-processor disabled">
									<Cpu />
								</el-icon>
							</el-col>
						</el-row>
					</el-form-item>
										
					<el-form-item label="Наименование" label-position="top" prop="externalName">
						<el-input v-model="ruleForm.externalName" maxlength="255" :readonly="!isManualFillingFormModeOn" />
					</el-form-item>

					<el-form-item label="Описание (внутреннее поле)" label-position="top">
						<el-input
							v-model="ruleForm.internalDescription"
							maxlength="5000"
							show-word-limit
							type="textarea"
							placeholder="Описание запчасти"
						/>
					</el-form-item>

					<el-form-item v-if="!isManualFillingFormModeOn" label="Производитель" label-position="top" prop="manufacturerId">
						<el-select 
							v-model="ruleForm.manufacturerId"
							placeholder="Выберите из списка">
							<el-option 
								v-for="item in ruleForm.manufacturers"
								:key="item.value"
								:value="item.value"
								:label="item.label" />
						</el-select>
					</el-form-item>	

					<el-form-item v-if="isManualFillingFormModeOn" label="Производитель" label-position="top" prop="manualManufacturer">
						<el-select
							filterable
							allow-create
							v-model="ruleForm.manualManufacturer"
							placeholder="Выберите из списка">
							<el-option
								v-for="item in itemManufacturers"
								:key="item.id"
								:value="item.id"
								:label="item.name">
							</el-option>
						</el-select>
					</el-form-item>	

					<el-form-item label="Единица измерения" label-position="top" prop="measureUnitId">
						<el-select
							v-model="ruleForm.measureUnitId"
							placeholder="Выберите из списка">
							<el-option
								v-for="item in itemMeasureUnits"
								:key="item.id"
								:value="item.id"
								:label="item.name">
							</el-option>
						</el-select>
					</el-form-item>

					<el-form-item label="Группа" label-position="top" prop="groupId">
						<tree-view-component 
							url="/api/itemsgroups"
							v-model="ruleForm.groupId"
							:disable-root="true"
						/>
					</el-form-item>

					<el-form-item>
						<el-switch
							v-model="isManualFillingFormModeOn"
							active-text="Ручной ввод"
						/>
					</el-form-item>

					<el-form-item>
						<div style="margin: .7rem 0;">
							<el-button type="primary" @click="onUpdateItemClick" v-if="!isCreatingMode">
								Сохранить
							</el-button>							
							<button-loading-component :loading="isAsyncOperationPending" label="Создать" @onClick="onCreateItemClick" v-else />
						</div>
					</el-form-item>
				</el-form>
			</div>
		</el-col>
	</el-row>

	<search-items-by-part-number-component
		@item-selected="onInternalCatalogItemSelected"
		@closed="() => itemsDialogVisible = false"
		:items="itemsDialogData"
		:part-number="itemsDialogPartNumber"
		:visible="itemsDialogVisible" />
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { Cpu, Loading } from '@element-plus/icons-vue';
import { ElButton, ElTable, ElLink, ElNotification, FormInstance } from 'element-plus';
import { reactive } from 'vue';
import axiosHelper from '@/helpers/axiosHelper';
import store from '@/store';
import ItemModel from '@/models/ItemModel';
import ItemGroupModel from '@/models/ItemGroupModel';
import ItemMeasureUnitModel from '@/models/ItemMeasureUnitModel';
import ItemManufacturerModel from '@/models/ItemManufacturerModel';
import EventHelper from '@/helpers/eventsHelper';
import ButtonLoadingComponent from '@/components/ButtonLoadingComponent.vue';
import SearchItemsByPartNumberComponent from '@/components/SearchItemsByPartNumberComponent.vue';
import TreeViewComponent from '@/components/TreeViewComponent.vue';
import LabelValuePair from '@/models/LabelValuePair';
import ItemsApiHelper from '@/helpers/api/ItemsApiHelper';

interface IItemForm {	
	id: number,
	externalName: string,
	internalDescription: string | null,
	partNumber: string,
	
	manualManufacturer: number | string | null,
	manufacturerId: number | null,
	measureUnitId: number | null,
	groupId: number | null,

	manufacturers: Array<LabelValuePair<string, number>> | null
}

@Options({
	components: {
		ElButton, ElTable, ElLink, Cpu, Loading, 
		ButtonLoadingComponent, 
		SearchItemsByPartNumberComponent,
		TreeViewComponent
	},
	props: {
		params: Array
	}
})
export default class ItemView extends Vue {
	/** params */
	params!: Array<any>;
	item: ItemModel | null = null;
	isCreatingMode = false;

	/** constants */
	itemNumberMinLength = 1;
	refreshEvent = 'itemsUpdate';
	itemGroups: Array<ItemGroupModel> = [];
	itemMeasureUnits: Array<ItemMeasureUnitModel> = [];
	itemManufacturers: Array<ItemManufacturerModel> | null = null;
	searchByNumberHttpClient = axiosHelper.createInstance();

	/** view-models */	
	isSearchByNumberLoading = false;

	itemsDialogPartNumber = '';
	itemsDialogVisible = false;
	itemsDialogData: Array<ItemModel> = [];

	isAsyncOperationPending = false;

	isManualFillingFormModeOn = false;
	isItemProcessed = false;
	ruleForm: IItemForm = {
		id: 0,
		externalName: '',
		internalDescription: null,
		partNumber: '',
		
		manualManufacturer: null,
		manufacturerId: null,
		measureUnitId: null,
		groupId: null,

		manufacturers: null
	};

	rules = reactive({
		partNumber: [
			{ required: true, message: 'Артикул не может быть пуст'},
			{ min: this.itemNumberMinLength, max: 255, message: 'Артикул не может быть короче 1 и длинее 255 символов'},
			{ validator: (validator: any, value: string, callback: any) => this.partNumberValidator(validator, value, callback) },
		],
		externalName: [			
			{ validator: (validator: any, value: string, callback: any) => this.externalNameValidator(validator, value, callback) },
		],
		measureUnitId: [			
			{ required: true, message: 'Укажите тип количественного измерения'},
		],
		groupId: [
			{ required: true, message: 'Укажите группу запчасти'},
		],
		manufacturerId: [
			{ required: true, message: 'Укажите производителя запчасти', trigger: 'blur' },
			{ validator: (validator: any, value: number, callback: any) => this.manufacturerValidator(validator, value, callback) },
		],
		manualManufacturer: [
			{ required: true, message: 'Укажите производителя запчасти', trigger: 'blur' },
			{ validator: (validator: any, value: any, callback: any) => this.manufacturerManualValidator(validator, value, callback) },
		]
	});
	
	mounted() {		
		this.isAsyncOperationPending = true;
		Promise
			.all([
				this.fetchMeasureUnitsAsync(),
				this.fetchManufacturersAsync()
			])
			.then(() => this.initView())
			.finally(() => this.isAsyncOperationPending = false);
	}

	/**
	 * init form with items's data from incoming parameters array (params)
	 */
	initView() {		
		if (!this.params?.length) {
			this.isCreatingMode = true;
			return;
		}

		this.isCreatingMode = this.params[1] === true;

		if (!this.isCreatingMode &&
			this.params[0]?.item?.id) {
			this.item = new ItemModel(this.params[0].item);
			this.ruleForm.id = this.item.id;
			this.ruleForm.externalName = this.item.externalName;
			this.ruleForm.internalDescription = this.item.internalDescription;
			this.ruleForm.partNumber = this.item.partNumber;			
			this.ruleForm.measureUnitId = this.item.measureUnit;
			this.ruleForm.groupId = this.item.group!.id;
			this.ruleForm.manufacturerId = this.item.manufacturer!.id;

			const itemManufacturer = this.findExistingManufacturerById(this.item.manufacturer!.id);
			if (itemManufacturer?.id) {
				const itemManufacturerPair = new LabelValuePair<string, number>({ label: itemManufacturer.name, value: itemManufacturer.id });				
				this.ruleForm.manufacturers = [ itemManufacturerPair ]
			}
		}

		if (this.isCreatingMode &&
			this.params[0]?.item) {
			this.item = new ItemModel(this.params[0].item);			
			this.onInternalCatalogItemSelected(this.item);
		}
	}

	async onUpdateItemClick() {		
		const form = this.$refs.addUserForm as FormInstance;
		form.validate(async (valid) => {
			if (valid) {
				this.isAsyncOperationPending = true;

				let manufacturerId = await this.tryGetOrCreatedManufacturerIdAsync();

				if (!manufacturerId) {
					ElNotification({
						message: 'Ошибка изменения запчасти: не удалость создать производителя',
						type: 'error',
					});
					console.error('Unable to update part: it is unable to get part\'s manufacturer');
					this.isAsyncOperationPending = true;
					return;
				}

				try {
					const request = {
						id: this.ruleForm.id,
						externalName: this.ruleForm.externalName,
						internalDescription: this.ruleForm.internalDescription,
						partNumber: this.ruleForm.partNumber,
						manufacturerId,
						measureUnitId:  this.ruleForm.measureUnitId,
						groupId: this.ruleForm.groupId
					};
					const updatedItem = await this.updateItemAsync(request);					
					ElNotification({
						message: `Запчасть '${this.ruleForm.partNumber}' успешно обновлена`,
						type: 'success',
					});
					EventHelper.dispatchEvent(this.refreshEvent);
					EventHelper.dispatchEvent('itemUpdated', updatedItem);
					this.closeCurrentTab();
				} catch (e: any) {
					if (e.code == 'ERR_NETWORK') {
						ElNotification({
							message: `Ошибка соединения, не удалось обновить запчасть`,
							type: 'error',
						});
					} else {
						if (e.response?.status == 409) {
							ElNotification({
								message: `Запчасть '${this.ruleForm.partNumber}' уже существует у выбранного производителя`,
								type: 'error',
							});
						} else {
							ElNotification({
								message: `Ошибка изменения запчасти`,
								type: 'error',
							});
						}
					}
					console.error(`Error updating item #${this.ruleForm.id}`, e);
				}	
				finally {
					this.isAsyncOperationPending = false;
				}		
			}
		});
	}

	async onCreateItemClick() {
		const form = this.$refs.addUserForm as FormInstance;
		form.validate(async (valid) => {
			if (valid) {
				this.isAsyncOperationPending = true;

				let manufacturerId = await this.tryGetOrCreatedManufacturerIdAsync();

				if (!manufacturerId) {
					ElNotification({
						message: `Ошибка создания запчасти: не удалость создать производителя`,
						type: 'error',
					});
					console.error('Unable to create part: it is unable to get part\'s manufacturer');
					this.isAsyncOperationPending = true;
					return;
				}

				try {
					const request = {
						externalName: this.ruleForm.externalName,
						internalDescription: this.ruleForm.internalDescription,
						partNumber: this.ruleForm.partNumber,
						manufacturerId,
						measureUnitId: this.ruleForm.measureUnitId,
						groupId: this.ruleForm.groupId
					};
					const createdItem = await this.addItemAsync(request);					
					ElNotification({
						message: `Запчасть '${this.ruleForm.partNumber}' успешно создана`,
						type: 'success',
					});
					EventHelper.dispatchEvent(this.refreshEvent);
					EventHelper.dispatchEvent('itemUpdated', createdItem);
					this.closeCurrentTab();
				} catch (e: any) {
					if (e.code == 'ERR_NETWORK') {
						ElNotification({
							message: `Ошибка соединения, не удалось создать новую запчасть`,
							type: 'error',
						});
					} else {
						if (e.response?.status == 409) {
							ElNotification({
								message: `Запчасть '${this.ruleForm.partNumber}' уже существует у выбранного производителя`,
								type: 'error',
							});
						} else {
							ElNotification({
								message: `Ошибка создания новой запчасти`,
								type: 'error',
							});
						}
					}
					console.error(`Error creating item`, e);
				} finally {
					this.isAsyncOperationPending = false;
				}
			}
		});
	}

	async partNumberValidator(rule: any, value: string, callback: any) {	
		/** don't validate if card-view mode and fields aren't dirty */
		if (this.item &&
			this.item.partNumber == this.ruleForm.partNumber &&
			this.item.manufacturer?.id ==  this.ruleForm.manufacturerId) {
			return true;
		}

		/* if manufacture has not been selected yet */
		if (!this.ruleForm.manufacturerId)	 {
			return true;
		}

		const isItemUnique = await this.isItemUniqueAsync(this.ruleForm.manufacturerId, value);

		if (!isItemUnique) {			
			const manufacturer = this.findExistingManufacturerById(this.ruleForm.manufacturerId) ?? null;
			if (manufacturer) {
				callback(new Error(`Запчасть '${value}' у производителя ${manufacturer.name} уже существует`));
			} else {
				callback(new Error(`Запчасть '${value}' уже существует`));
			}
			return false;
		}

		return true;
	}

	externalNameValidator(rule: any, externalName: string, callback: any) {
		if (this.isManualFillingFormModeOn) {
			if (!externalName.length) {
				callback(new Error('Наименование артикула должно быть указано'));
				return false;
			}
		}

		return true;
	}

	async manufacturerValidator(rule: any, manufacturerId: number, callback: any) {
		/** don't validate if card-view mode and fields aren't dirty */
		if (this.item &&
			this.item.partNumber == this.ruleForm.partNumber &&
			this.item.manufacturer?.id ==  this.ruleForm.manufacturerId ||
			manufacturerId === 0) {
			return true;
		}

		if (!this.ruleForm.partNumber ||
			!this.ruleForm.partNumber.length) {
			return true;
		}

		const isItemUnique = await this.isItemUniqueAsync(manufacturerId, this.ruleForm.partNumber);

		if (!isItemUnique) {
			callback(new Error(`Запчасть '${this.ruleForm.partNumber}' уже существует у этого производителя`));
			return false;
		}

		return true;
	}

	async manufacturerManualValidator(rule: any, manufacturer: any, callback: any) {		
		/** don't validate if card-view mode and fields aren't dirty */
		if (this.item &&
			this.item.partNumber == this.ruleForm.partNumber &&
			this.item.manufacturer?.id == this.ruleForm.manufacturerId ||
			!manufacturer) {
			return true;
		}


		if (!this.ruleForm.manualManufacturer) {
			callback(new Error('Укажите производителя запчасти'));
			return false;
		}

		if (this.ruleForm.partNumber?.length) {
			let isItemUnique;

			if (typeof this.ruleForm.manualManufacturer === 'string') {
				isItemUnique = await this.isItemUniqueByManufacturerNameAsync(this.ruleForm.manualManufacturer, this.ruleForm.partNumber);
			} 

			if (typeof this.ruleForm.manualManufacturer === 'number') {
				isItemUnique = await this.isItemUniqueAsync(this.ruleForm.manualManufacturer, this.ruleForm.partNumber);
			}

			if (!isItemUnique) {
				callback(new Error(`Запчасть '${this.ruleForm.partNumber}' уже существует у этого производителя`));
				return false;
			}
		}

		return true;
	}

	closeCurrentTab() {		
		store.commit('removeTab', store.getters.tab);
	}

	async fetchManufacturersAsync() {
		const httpClient = axiosHelper.createInstance(store.getters.jwt);
		try {
			const response = await httpClient.get('/api/items/manufacturers');
			if (!response.data?.length) {
				this.itemManufacturers = [];
				return;
			}
			this.itemManufacturers = response.data.map((_: any) => new ItemManufacturerModel(_));
		} catch (e) {
			ElNotification({
				message: 'Не удалось загрузить список производителей',
				type: 'error',
			});
			console.error('Error loading item manufacturers list', e);
		}
	}

	async fetchMeasureUnitsAsync() {
		const httpClient = axiosHelper.createInstance(store.getters.jwt);
		try {
			const response = await httpClient.get('/api/items/measureunits');
			if (!response.data?.length) {
				this.itemMeasureUnits = [];
				return;
			}
			this.itemMeasureUnits = response.data.map((_: any) => new ItemMeasureUnitModel(_));
		} catch (e) {
			ElNotification({
				message: 'Не удалось загрузить единицы измерения',
				type: 'error',
			});
			console.error('Error loading item measure units list', e);
		}
	}

	/** try to get an existing manufacturer ID or create a new one get its ID */
	async tryGetOrCreatedManufacturerIdAsync() : Promise<number | null> {
		const createManufacturerRequest = async (manufacturerName: string) => {
			const manufacturersHttpClient = axiosHelper.createInstance(store.getters.jwt);				
			const addManufacturerResponse = await manufacturersHttpClient.post('/api/items/manufacturer', {
				name: manufacturerName
			});

			if (!addManufacturerResponse?.data?.id) {				
				return null;
			}

			return new ItemManufacturerModel(addManufacturerResponse.data).id;
		};

		let manufacturerId: number | null = null;

		if (this.isManualFillingFormModeOn) {
			if (typeof this.ruleForm.manualManufacturer === 'string') {
				manufacturerId = await createManufacturerRequest(this.ruleForm.manualManufacturer);				
			}
			if (typeof this.ruleForm.manualManufacturer === 'number') {
				manufacturerId = this.ruleForm.manualManufacturer;
			}
		} else {
			if (this.ruleForm.manufacturerId == 0) {
				const newManufacturerName = this.ruleForm.manufacturers![0].label;
				manufacturerId = await createManufacturerRequest(newManufacturerName);
			} else {
				manufacturerId = this.ruleForm.manufacturerId!;
			}
		}
		
		return manufacturerId;
	}

	async isItemUniqueByManufacturerNameAsync(manufacturerName: string, partNumber: string) {		
		try {			
			const httpClient = axiosHelper.createInstance(store.getters.jwt);
			await httpClient.get(`/api/items/by-manufacturer-name/${manufacturerName}/${partNumber}`);
			return true;
		} catch (e) {
			return false;
		}
	}

	async isItemUniqueAsync(manufacturerId: number, partNumber: string) {
		const httpClient = axiosHelper.createInstance(store.getters.jwt);
		try {
			await httpClient.get(`/api/items/${manufacturerId}/${partNumber}`);
			return true;
		} catch (e) {
			return false;
		}
	}

	async updateItemAsync(itemUpdteRequest: any) : Promise<ItemModel> {
		const jwt = store.getters.jwt;
		const itemsHttpClient = axiosHelper.createInstance(jwt);
		const response = await itemsHttpClient.put('/api/items', itemUpdteRequest);
		const updatedItem = new ItemModel(response.data);
		return updatedItem;
	}

	async addItemAsync(itemAddRequest: any) : Promise<ItemModel> {
		const jwt = store.getters.jwt;
		const itemsHttpClient = axiosHelper.createInstance(jwt);
		const response = await itemsHttpClient.post('/api/items', itemAddRequest);
		const createdItem = new ItemModel(response.data);
		return createdItem;
	}

	/** fires when user filled up partNumber field and presses Enter button */
	onPartNumberKeyup(e: KeyboardEvent) {
		if (this.ruleForm.partNumber?.length < this.itemNumberMinLength) {
			return;
		}

		if (this.isSearchByNumberLoading) {
			return;
		}

		if ((e.key === "Enter" || e.which == 13) && 
			!this.isManualFillingFormModeOn) {			
			e.preventDefault();		
			this.showExternalItemsPickerDialogAsync();
			return;
		}

		if (this.isItemProcessed) {
			this.clearForm();
		}
	}

	onPartNumberClear() {
		this.clearForm();
	}

	/** fires when user filled up partNumber field and clicks on Processor icon */
	onPartNumberProcessClick(partNumber: string) {
		const isDisabled = partNumber?.length < this.itemNumberMinLength;

		if (isDisabled ||
			this.isSearchByNumberLoading) {
			return;
		}

		this.itemsDialogVisible = true;
		this.showExternalItemsPickerDialogAsync();
	}

	/** fires when user picked an item from external catalog seach results */
	onInternalCatalogItemSelected(item: ItemModel) {
		this.ruleForm.partNumber = item.partNumber;
		this.ruleForm.externalName = item.externalName;

		const manufaturerId = this.findExistingManufacturerByName(item);

		if (!manufaturerId) {
			this.ruleForm.manufacturerId = 0;
			this.ruleForm.manufacturers = [ new LabelValuePair<string, number>({ label: item.manufacturer?.name, value: 0 }) ];
		} else {
			this.ruleForm.manufacturerId = manufaturerId;
			this.ruleForm.manufacturers = [ new LabelValuePair<string, number>({ label: item.manufacturer?.name, value: manufaturerId }) ];
		}

		this.ruleForm.internalDescription = `${item.partNumber}, ${item.manufacturer?.name}\r\n${item.externalName}`;
		this.itemsDialogVisible = false;
		this.isItemProcessed = true;
	}

	/** search across loaded array of items manufacturers for a particular manufacturer by its name */
	/** @returns id of an existing manufacturer or undefined if no manufacturer is found */
	findExistingManufacturerByName(item: ItemModel) : number | undefined {
		if (!this.itemManufacturers) {
			return undefined;
		}

		const manufactureNameLower = item.manufacturer!.name.toLowerCase().trim();		
		const manufaturerId = this.itemManufacturers.find(_ => _.name.toLowerCase() == manufactureNameLower)?.id;
		return manufaturerId;
	}

	/** search across loaded array of items manufacturers for a particular manufacturer by its ID */
	/** @returns {ItemManufacturerModel} if manufacturer with exact ID is found */
	findExistingManufacturerById(manufacturerId: number) : ItemManufacturerModel | undefined {
		if (!this.itemManufacturers) {
			return undefined;
		}
		return this.itemManufacturers.find(_ => _.id == manufacturerId);
	}

	clearForm() {
		this.isItemProcessed = false;
		this.ruleForm.partNumber = '';
		this.ruleForm.externalName = '';
		this.ruleForm.internalDescription = '';
	
		this.ruleForm.manufacturerId = null;
		this.ruleForm.measureUnitId = null;
		this.ruleForm.groupId = null;
		this.ruleForm.manufacturers = null;
	}

	async showExternalItemsPickerDialogAsync() : Promise<void> {
		this.isSearchByNumberLoading = true;
		const itemsApiHelper = new ItemsApiHelper(store.getters.jwt);		
		const foundItems = await itemsApiHelper.externalSearchItemsByPartNumber(this.ruleForm.partNumber);

		if (foundItems == null) {
			ElNotification({
				message: `Ошибка поиска автозапчастей по артикулу '${this.ruleForm.partNumber}'`,
				type: 'error',
			});
			this.isSearchByNumberLoading = false;
			return;
		} 
		
		if (!foundItems.length) {
			ElNotification({
				message: `Поиск автозапчастей по артикулу '${this.ruleForm.partNumber}' не дал результатов`,
				type: 'warning',
			});
			this.isSearchByNumberLoading = false;
			return;
		}

		this.itemsDialogData = foundItems!;
		this.itemsDialogPartNumber = this.ruleForm.partNumber;
		await this.$nextTick();
		this.itemsDialogVisible = true;
		this.isSearchByNumberLoading = false;
	}
}
</script>

<style scoped>
h2.item span {
	color: var(--el-color-primary);
}

.item-processor {	
	cursor: pointer;
	color: var(--el-color-primary);
}

	.item-processor.disabled {
		cursor: default;
		color: var(--el-color-info-light-3);
	}

	.item-processor:active:not(.disabled) {
		color: var(--el-color-primary-light-7);
	}
</style>